import { MAP_H, MAP_W, SPOTS, tileAt } from "./map.ts";
import { TRAINERS, trainerAt, type TrainerId } from "./trainers.ts";

export interface MapPin {
  id: string;
  name: string;
  x: number;
  y: number;
  kind: "service" | "rest" | "trainer";
  trainer?: TrainerId;
}

export const MAP_PINS: MapPin[] = [
  ...SPOTS.filter(s => ["heal", "shop", "pc", "rest", "market", "raid", "crowns"].includes(s.kind)).map(s => ({
    id: `${s.kind}:${s.x},${s.y}`, name: s.title, x: s.x, y: s.y,
    kind: s.kind === "rest" ? "rest" as const : "service" as const,
  })),
  ...Object.values(TRAINERS).map(t => ({ id: t.id, name: t.name, x: t.x, y: t.y, kind: "trainer" as const, trainer: t.id })),
];

export interface MapRoute {
  tiles: { x: number; y: number }[];
  grassSteps: number;
}

/** Prefer fewer grass steps, then the shortest walk; never cross solid tiles or trainers. */
export function routeToPin(start: { x: number; y: number }, pin: MapPin): MapRoute | null {
  const key = (x: number, y: number) => y * MAP_W + x;
  const startKey = key(start.x, start.y);
  const costs = new Map<number, number>([[startKey, 0]]);
  const previous = new Map<number, number>();
  const queue = [{ ...start, cost: 0 }];
  const reached = (x: number, y: number) => pin.kind === "trainer"
    ? Math.abs(x - pin.x) + Math.abs(y - pin.y) === 1 : x === pin.x && y === pin.y;
  while (queue.length) {
    queue.sort((a, b) => b.cost - a.cost);
    const at = queue.pop()!, atKey = key(at.x, at.y);
    if (at.cost !== costs.get(atKey)) continue;
    if (reached(at.x, at.y)) {
      const tiles = [{ x: at.x, y: at.y }];
      let back = atKey;
      while (back !== startKey) {
        back = previous.get(back)!;
        tiles.unshift({ x: back % MAP_W, y: Math.floor(back / MAP_W) });
      }
      return { tiles, grassSteps: tiles.slice(1).filter(t => tileAt(t.x, t.y).tall).length };
    }
    for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
      const x = at.x + dx!, y = at.y + dy!, tile = tileAt(x, y);
      if (tile.solid || trainerAt(x, y)) continue;
      const next = key(x, y), cost = at.cost + 1 + (tile.tall ? MAP_W * MAP_H + 1 : 0);
      if (cost >= (costs.get(next) ?? Infinity)) continue;
      costs.set(next, cost); previous.set(next, atKey); queue.push({ x, y, cost });
    }
  }
  return null;
}
